import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { techAPI } from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Phone, MapPin, Star, CheckCircle2, Clock,
  Loader2, FileText, ChevronDown, ChevronUp, DollarSign,
  AlertTriangle,
} from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-600',
  sent: 'bg-blue-100 text-blue-700',
  presented: 'bg-blue-100 text-blue-700',
  awaiting_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700',
  expired: 'bg-slate-100 text-slate-500',
  converted_to_job: 'bg-emerald-100 text-emerald-700',
  copied_to_job: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-emerald-100 text-emerald-700',
};

const STATUS_LABELS = {
  draft: 'Draft',
  unscheduled: 'Unscheduled',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  sent: 'Sent',
  presented: 'Presented',
  awaiting_approval: 'Awaiting Approval',
  approved: 'Approved',
  declined: 'Declined',
  expired: 'Expired',
  converted_to_job: 'Converted',
  copied_to_job: 'Copied to Job',
  completed: 'Completed',
};

export default function TechEstimateDetail() {
  const { estimateId } = useParams();
  const navigate = useNavigate();
  const [est, setEst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedOption, setExpandedOption] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await techAPI.getEstimateDetail(estimateId);
      setEst(res.data);
      // Auto-expand recommended or selected option
      const sel = res.data.options.find(o => o.is_selected);
      const rec = res.data.options.find(o => o.is_recommended);
      setExpandedOption(sel?.id || rec?.id || res.data.options[0]?.id);
    } catch {
      toast.error('Failed to load estimate');
    } finally {
      setLoading(false);
    }
  }, [estimateId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (!est) {
    return (
      <div className="px-4 pt-6 text-center">
        <p className="text-slate-400">Estimate not found</p>
        <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-slate-100 rounded-lg text-sm font-bold">Go Back</button>
      </div>
    );
  }

  const statusColor = STATUS_COLORS[est.status] || 'bg-slate-100 text-slate-600';
  const statusLabel = STATUS_LABELS[est.status] || est.status;
  const isApproved = est.status === 'approved' || est.status === 'converted_to_job' || est.status === 'copied_to_job' || est.status === 'completed';
  const isPending = est.status === 'sent' || est.status === 'presented' || est.status === 'awaiting_approval';
  const isDeclined = est.status === 'declined';

  return (
    <div className="max-w-lg mx-auto pb-4" data-testid="tech-estimate-detail">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-white border-b-2 border-slate-900 px-4 py-3">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-lg active:bg-slate-100" data-testid="estimate-back">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="text-center">
            <p className="text-xs text-slate-400 font-mono">#{est.estimate_number}</p>
            <p className="text-sm font-bold text-slate-950">{est.estimate_name || 'Estimate'}</p>
          </div>
          <Badge className={`text-[10px] font-bold uppercase tracking-wide ${statusColor}`} data-testid="estimate-status">
            {statusLabel}
          </Badge>
        </div>
      </div>

      {/* Approval banner */}
      {isApproved && (
        <div className="mx-4 mt-3 p-3 bg-emerald-50 border-2 border-emerald-300 rounded-lg flex items-center gap-3" data-testid="approval-banner">
          <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
          <div>
            <p className="text-sm font-bold text-emerald-800">Approved</p>
            {est.approval_name && <p className="text-xs text-emerald-600">by {est.approval_name}</p>}
            {est.approved_at && (
              <p className="text-[10px] text-emerald-500 mt-0.5">
                {new Date(est.approved_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
            )}
          </div>
        </div>
      )}

      {isPending && (
        <div className="mx-4 mt-3 p-3 bg-amber-50 border-2 border-amber-300 rounded-lg flex items-center gap-3" data-testid="pending-banner">
          <Clock className="w-6 h-6 text-amber-600 shrink-0" />
          <div>
            <p className="text-sm font-bold text-amber-800">Awaiting Customer Approval</p>
            <p className="text-xs text-amber-600">Sent to customer for review</p>
          </div>
        </div>
      )}

      {isDeclined && (
        <div className="mx-4 mt-3 p-3 bg-red-50 border-2 border-red-300 rounded-lg flex items-center gap-3" data-testid="declined-banner">
          <AlertTriangle className="w-6 h-6 text-red-600 shrink-0" />
          <div>
            <p className="text-sm font-bold text-red-800">Declined</p>
            <p className="text-xs text-red-600">Customer declined this estimate</p>
          </div>
        </div>
      )}

      {/* Customer */}
      {est.customer_name && (
        <div className="mx-4 mt-3 p-3 bg-white border border-slate-200 rounded-lg" data-testid="estimate-customer">
          <p className="text-sm font-bold text-slate-950">{est.customer_name}</p>
          {est.customer_address && (
            <p className="text-xs text-slate-500 flex items-start gap-1 mt-0.5">
              <MapPin className="w-3 h-3 mt-0.5 shrink-0" /> {est.customer_address}
            </p>
          )}
          {est.customer_phone && (
            <a href={`tel:${est.customer_phone}`} className="inline-flex items-center gap-1 mt-1.5 text-xs font-bold text-blue-600">
              <Phone className="w-3 h-3" /> {est.customer_phone}
            </a>
          )}
        </div>
      )}

      {/* Total */}
      <div className="mx-4 mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-between">
        <span className="text-sm text-slate-600">Estimate Total</span>
        <span className="text-lg font-bold font-mono text-slate-950">${est.total.toFixed(2)}</span>
      </div>

      {/* Options */}
      <div className="mx-4 mt-4">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 px-0.5">
          Options {est.options.length > 1 && `(${est.options.length})`}
        </p>
        <div className="space-y-2">
          {est.options.map(opt => {
            const isExpanded = expandedOption === opt.id;
            return (
              <div key={opt.id}
                className={`rounded-lg border-2 overflow-hidden transition-colors ${
                  opt.is_selected ? 'border-emerald-400 bg-emerald-50/30' :
                  opt.is_recommended ? 'border-blue-300 bg-blue-50/30' :
                  'border-slate-200 bg-white'
                }`}
                data-testid={`estimate-option-${opt.id}`}
              >
                <button
                  onClick={() => setExpandedOption(isExpanded ? null : opt.id)}
                  className="w-full text-left px-4 py-3 flex items-center justify-between active:bg-black/5"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {opt.is_selected && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />}
                    {opt.is_recommended && !opt.is_selected && <Star className="w-4 h-4 text-blue-500 shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-950 truncate">{opt.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {opt.is_selected && <span className="text-[10px] font-bold text-emerald-600 uppercase">Selected</span>}
                        {opt.is_recommended && !opt.is_selected && <span className="text-[10px] font-bold text-blue-600 uppercase">Recommended</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-base font-bold font-mono text-slate-950">${opt.total.toFixed(2)}</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-3 border-t border-slate-100">
                    {opt.description && <p className="text-xs text-slate-500 py-2">{opt.description}</p>}
                    {opt.recommendation_reason && (
                      <p className="text-xs text-blue-600 italic py-1">{opt.recommendation_reason}</p>
                    )}
                    {opt.items.length > 0 && (
                      <div className="space-y-1 mt-1">
                        {opt.items.map((item, i) => (
                          <div key={i} className="flex items-start justify-between py-1.5 border-b border-slate-100 last:border-0">
                            <div className="flex-1 min-w-0 pr-2">
                              <p className="text-sm text-slate-700">{item.name}</p>
                              {item.description && <p className="text-[10px] text-slate-400 mt-0.5 truncate">{item.description}</p>}
                              <p className="text-[10px] text-slate-400">
                                {item.quantity > 1 && `${item.quantity} × `}${item.unit_price.toFixed(2)}
                              </p>
                            </div>
                            <span className="text-sm font-mono text-slate-600">${item.total_price.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Subtotal / tax / total */}
                    <div className="mt-2 pt-2 border-t border-slate-200 space-y-1">
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>Subtotal</span>
                        <span className="font-mono">${opt.subtotal.toFixed(2)}</span>
                      </div>
                      {opt.tax_amount > 0 && (
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>Tax</span>
                          <span className="font-mono">${opt.tax_amount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm font-bold text-slate-950">
                        <span>Total</span>
                        <span className="font-mono">${opt.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Upsells */}
      {est.upsells.length > 0 && (
        <div className="mx-4 mt-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 px-0.5">Add-Ons</p>
          <div className="space-y-1.5">
            {est.upsells.map(u => (
              <div key={u.id} className="flex items-center justify-between px-3 py-2.5 bg-white border border-slate-200 rounded-lg">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{u.name}</p>
                  {u.description && <p className="text-[10px] text-slate-400 truncate">{u.description}</p>}
                </div>
                <span className="text-sm font-mono font-bold text-slate-700 shrink-0">${u.price.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Deposit */}
      {est.deposit_type && est.deposit_type !== 'none' && est.deposit_amount > 0 && (
        <div className="mx-4 mt-4 p-3 border border-slate-200 rounded-lg" data-testid="deposit-section">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-600">Deposit Required</p>
              <p className="text-sm font-mono font-bold text-slate-950">
                ${est.deposit_amount.toFixed(2)}
                {est.deposit_type === 'percentage' && <span className="text-slate-400 text-xs"> (%)</span>}
              </p>
            </div>
            <Badge className={est.deposit_collected ? 'bg-emerald-100 text-emerald-700 text-xs font-bold' : 'bg-amber-100 text-amber-700 text-xs font-bold'}>
              {est.deposit_collected ? 'Collected' : 'Not Collected'}
            </Badge>
          </div>
        </div>
      )}

      {/* Notes */}
      {est.notes.length > 0 && (
        <div className="mx-4 mt-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 px-0.5">Notes</p>
          <div className="space-y-1.5">
            {est.notes.map(n => (
              <div key={n.id} className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-sm text-slate-700">{n.content}</p>
                <p className="text-[10px] text-slate-400 mt-1">
                  {n.author_name && `${n.author_name} · `}
                  {n.created_at && new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Description */}
      {est.description && (
        <div className="mx-4 mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Description</p>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{est.description}</p>
        </div>
      )}
    </div>
  );
}
